import { useContext, useEffect, useState } from "react";
import { getBudgetYearForProperty, useProperties } from "../../../contexts/properties/PropertiesContext";
import { useSettings } from "../../../contexts/settings/SettingsContext";
import {
    PropertyPerformanceModel,
    useGetPropertyPerformanceLazyQuery,
    VersionType
} from "../../../__generated__/generated_types";
import { TFocusYear } from "../../../global/types";
import { RentsDataConfig } from "./logic/RentsDataConfig";
import { Property, propertyDisplayBudgetYear } from "../../../contexts/properties/logic";
import {AuthContext} from "../../../contexts/AuthContext";

interface IPlanningDashboardData {
    data: PropertyPerformanceModel|undefined,
    loading: boolean,
    property: Property | undefined,
    budgetYear: number,
    focusYear: TFocusYear|undefined,
    setFocusYear: (focusYear: TFocusYear) => void,

    rentsPrefs: IRentPrefs,
    setRentsPrefs: (prefs:IRentPrefs) => void,

    fetch: () => void,
}

export default function usePlanningDashboardData(): IPlanningDashboardData {

    const auth = useContext(AuthContext);
    const settings = useSettings();
    const properties = useProperties();

    const [_fetch, { data, loading }] = useGetPropertyPerformanceLazyQuery({
        fetchPolicy: "network-only",
    });

    const budgetYear = getBudgetYearForProperty(properties.currentProperty, auth.budgetYear);

    useEffect(
        () => {
            if(settings.isDefaults || !settings.year || !properties.currentProperty){
                return;
            }
            // Default to the account's Budget year
            _setFocusYear({
                year: propertyDisplayBudgetYear(properties.currentProperty),
                type: VersionType.Budget,
            });

            _fetch({
                variables: {
                    propertyId: properties.currentProperty.id,
                    budgetYear: budgetYear,
                }
            });
        },
        [settings.isDefaults, settings.year, properties.currentProperty, budgetYear]
    );

    const [focusYear, _setFocusYear] = useState<TFocusYear>();
    const setFocusYear = (focusYear: TFocusYear): void => {
        _setFocusYear(focusYear);
    };

    /**
     * Preferences for the Rents Widget.
     * lookbackYears    How many years back the user would like to see in the Rents widget.
     */
    const [rentsPrefs, _setRentsPrefs] = useState<IRentPrefs>({
        lookbackYears: RentsDataConfig.maxLookbackYears,
    });

    const setRentsPrefs = (prefs:IRentPrefs):void => {
        const lookbackYears = prefs.lookbackYears > RentsDataConfig.maxLookbackYears
            ? RentsDataConfig.maxLookbackYears
            : prefs.lookbackYears;

        _setRentsPrefs({
            lookbackYears
        });
    };

    // Note: This is here to facilitate development. Remove when page is ready.
    const fetch = ():void => {
        if(settings.isDefaults || !settings.year || !properties.currentProperty){
            return;
        }
        _fetch({
            variables: {
                propertyId: properties.currentProperty.id,
                budgetYear: budgetYear,
            }
        });
    };

    // Note: loading represents the ready state of all dependencies, including performance data
    return {
        data: data?.getPropertyPerformance,
        loading: loading || settings.isDefaults || !settings.year || !properties.currentProperty,
        property: properties.currentProperty,
        budgetYear: budgetYear,
        focusYear,
        setFocusYear,

        rentsPrefs,
        setRentsPrefs,

        fetch,
    };
}

export interface IRentPrefs {
    lookbackYears: number,
}
